import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { useBrandLogos } from '../lib/useBrandLogos';

const navLinks = [
  { path: '/svt-co', label: 'Home', end: true },
  { path: '/svt-co/about', label: 'About Us' },
  { path: '/svt-co/leadership', label: 'Leadership' },
  { path: '/svt-co/contact', label: 'Contact Us' },
];

export default function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { companyLogo } = useBrandLogos();
  const logoSrc = companyLogo || '/images/logo.png';

  return (
    <div className="min-h-screen bg-white font-body">
      {/* Top Bar */}
      <div className="bg-[#1B2A1B] text-olive-300 text-xs py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="back-to-group">
              ← SVT Aaventures Group
            </Link>
            <span className="flex items-center gap-1.5"><Phone size={11} /> +91 98765 43210</span>
            <span className="flex items-center gap-1.5"><Mail size={11} /> info@svtandco.com</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin size={11} /> Whitefield, Bengaluru
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E6EDE6] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/svt-co" className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt="SVT & Co"
              className="w-10 h-10 object-contain"
              data-testid="svtco-header-logo"
            />
            <div>
              <span className="font-heading text-xl font-semibold text-[#1B2A1B]">SVT & Co</span>
              <span className="block text-[9px] tracking-[0.15em] uppercase text-olive-500 -mt-0.5">Business Strategy, Consulting &amp; Development</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.end}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-[#D4AF37] bg-[#D4AF37]/5'
                      : 'text-[#3B5A3B] hover:text-[#1B2A1B] hover:bg-olive-50'
                  }`
                }
                data-testid={`pub-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/login"
              className="ml-4 px-5 py-2 bg-[#1B2A1B] text-white text-sm font-medium rounded-sm hover:bg-[#2F472F] transition-all duration-200"
              data-testid="admin-login-btn"
            >
              Admin Login
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-sm hover:bg-olive-50"
            data-testid="mobile-nav-btn"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-[#E6EDE6] bg-white px-6 py-4 space-y-1">
            {navLinks.map(link => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-sm text-sm font-medium ${
                    isActive ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-[#3B5A3B]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="block px-4 py-2.5 text-sm font-medium text-[#1B2A1B] bg-olive-50 rounded-sm mt-2"
            >
              Admin Login
            </Link>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#1B2A1B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={logoSrc}
                  alt="SVT & Co"
                  className="w-10 h-10 object-contain"
                  data-testid="svtco-footer-logo"
                />
                <div>
                  <p className="font-heading text-lg font-semibold text-[#D4AF37]">SVT & Co</p>
                  <p className="text-[9px] tracking-[0.15em] uppercase text-olive-400">Business Strategy, Consulting &amp; Development</p>
                </div>
              </div>
              <p className="text-sm text-olive-300 leading-relaxed">
                Your trusted tax partner in Bengaluru. We provide comprehensive tax consulting,
                compliance, and advisory services to individuals and businesses across India.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-heading text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Quick Links</h3>
              <div className="space-y-2.5">
                {navLinks.map(link => (
                  <Link key={link.path} to={link.path} className="block text-sm text-olive-300 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                ))}
                <Link to="/login" className="block text-sm text-olive-300 hover:text-white transition-colors">
                  Client Portal
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Contact Us</h3>
              <div className="space-y-3 text-sm text-olive-300">
                <div className="flex gap-3">
                  <MapPin size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>SVT & Co., near Adhishakthi Royal Grove, Sector-1, 15th Cross, Opp BBMP Office, Varthur Main Rd, Ramagondanahalli, Whitefield, Bengaluru, Karnataka 560066</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span>+91 98765 43210</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span>info@svtandco.com</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#2F472F] mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-olive-500">&copy; {new Date().getFullYear()} SVT & Co. All rights reserved.</p>
            <p className="text-xs text-olive-500">Business Strategy, Consulting &amp; Development Consultants &bull; Whitefield, Bengaluru</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
